import { IdSchema } from "@hisani/core";
import { z } from "zod";
export const PLANNER_ERROR_CODES = {
    conflict: "PLANNER_CONFLICT",
    importConflict: "PLANNER_IMPORT_IDEMPOTENCY_CONFLICT",
    inactivePlace: "PLANNER_INACTIVE_PLACE",
    invalidRequest: "PLANNER_INVALID_REQUEST",
    invalidTransition: "PLANNER_INVALID_TRANSITION",
    notFound: "PLANNER_NOT_FOUND",
    unavailable: "PLANNER_UNAVAILABLE",
};
export const SurfaceSchema = z.enum(["DIRT", "TURF", "ALL_WEATHER"]);
export const PlannerSortSchema = z.enum(["NEWEST", "OLDEST"]);
export const WeekdaySchema = z.enum([
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
    "SATURDAY",
    "SUNDAY",
]);
export const BreedSchema = z.enum([
    "OPEN",
    "THOROUGHBRED",
    "PUREBRED_ARABIAN",
    "OTHER",
]);
export const ConditionBookStatusSchema = z.enum([
    "DRAFT",
    "IN_REVIEW",
    "PUBLISHED",
    "REJECTED",
]);
export const SourceContentTypeSchema = z.enum([
    "application/pdf",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "text/csv",
]);
export const PLANNER_CSV_MAX_SOURCE_BYTES = 1024 * 1024;
export const PLANNER_CSV_MAX_ROWS = 1_000;
export const PLANNER_CSV_HEADERS = [
    "track",
    "meeting_date",
    "weekday",
    "meeting_number",
    "race_number",
    "race_name",
    "breed",
    "category",
    "grade",
    "handicap_low",
    "handicap_high",
    "benchmark_rating",
    "distance_meters",
    "surface",
    "age_restriction",
    "min_age",
    "sex_restriction",
    "uae_bred_only",
    "rated_minimum",
    "private_owners_only",
    "invitation_only",
    "purse_currency",
    "purse_amount",
    "purse_aed_equivalent",
    "entry_fee",
    "declaration_fee",
    "base_weight",
    "penalties",
    "allowances",
    "eligibility",
    "notes",
    "rating_qualification_published",
    "weights_published",
    "entries_close",
    "declarations_close",
    "jockey_declarations_close",
    "season",
];
const RaceDateSchema = z.string().date();
const persistedText = (schema) => schema.refine((value) => !value.includes("\0"));
const nonBlankPersistedText = (maximumLength) => persistedText(z.string().trim().min(1).max(maximumLength));
const GradeSchema = nonBlankPersistedText(120);
const DistanceMetersSchema = z.number().int().min(100).max(10_000);
const nullableText = (maximumLength) => nonBlankPersistedText(maximumLength).nullable();
const nullableInteger = (minimum, maximum) => z.number().int().min(minimum).max(maximum).nullable();
const PurseMinorSchema = z
    .string()
    .refine((value) => /^\d{1,19}$/.test(value) && BigInt(value) <= 9223372036854775807n);
const ISO_4217_CURRENCIES = new Set(Intl.supportedValuesOf("currency"));
export const PLANNER_CURRENCIES_WITHOUT_MINOR_UNITS = ["XDR", "XSU"];
const PLANNER_CURRENCIES_WITHOUT_MINOR_UNITS_SET = new Set(PLANNER_CURRENCIES_WITHOUT_MINOR_UNITS);
const IsoCurrencySchema = z
    .string()
    .regex(/^[A-Za-z]{3}$/)
    .transform((value) => value.toUpperCase())
    .refine((value) => ISO_4217_CURRENCIES.has(value));
export const PlannerPurseCurrencySchema = IsoCurrencySchema.refine((value) => !PLANNER_CURRENCIES_WITHOUT_MINOR_UNITS_SET.has(value));
const SeasonSchema = nonBlankPersistedText(80);
export const RaceFieldsSchema = z
    .object({
    raceDate: RaceDateSchema,
    track: nonBlankPersistedText(120),
    weekday: WeekdaySchema,
    meetingNumber: nullableInteger(1, 999),
    raceNumber: z.number().int().min(1).max(99),
    raceName: nullableText(200),
    breed: BreedSchema,
    category: nonBlankPersistedText(120),
    grade: GradeSchema.nullable(),
    handicapLow: nullableInteger(0, 200),
    handicapHigh: nullableInteger(0, 200),
    benchmarkRating: nullableInteger(0, 200),
    distanceMeters: DistanceMetersSchema.nullable(),
    surface: SurfaceSchema.nullable(),
    ageRestriction: nullableText(80),
    minAge: nullableInteger(1, 30),
    sexRestriction: nonBlankPersistedText(120),
    uaeBredOnly: z.boolean(),
    ratedMinimum: nullableInteger(0, 200),
    privateOwnersOnly: z.boolean(),
    invitationOnly: z.boolean(),
    purseMinor: PurseMinorSchema.nullable(),
    currency: PlannerPurseCurrencySchema.nullable(),
    purseAedMinor: PurseMinorSchema.nullable(),
    entryFee: nullableText(200),
    declarationFee: nullableText(200),
    baseWeight: nullableText(500),
    penalties: nullableText(2_000),
    allowances: nullableText(1_000),
    eligibility: nullableText(2_000),
    notes: nullableText(2_000),
    ratingQualificationPublished: RaceDateSchema.nullable(),
    weightsPublished: RaceDateSchema.nullable(),
    entriesClose: z.string().datetime({ offset: true }).nullable(),
    declarationsClose: z.string().datetime({ offset: true }).nullable(),
    jockeyDeclarationsClose: z.string().datetime({ offset: true }).nullable(),
    season: SeasonSchema,
})
    .strict()
    .superRefine((value, context) => {
    if ((value.surface === null) !== (value.distanceMeters === null)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: [value.surface === null ? "surface" : "distanceMeters"],
            message: "Surface and distance must be supplied together.",
        });
    }
    if (value.handicapLow !== null &&
        value.handicapHigh !== null &&
        value.handicapLow > value.handicapHigh) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["handicapHigh"],
            message: "Handicap high must not be below handicap low.",
        });
    }
    const hasPurse = value.purseMinor !== null;
    if (hasPurse !== (value.purseAedMinor !== null) ||
        (hasPurse && value.currency === null)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["purseMinor"],
            message: "Purse amount and AED equivalent must align and require a currency.",
        });
    }
});
export const LegacyRaceFieldsSchema = z.object({
    raceDate: RaceDateSchema,
    grade: GradeSchema,
    surface: SurfaceSchema,
    distanceMeters: DistanceMetersSchema,
    breed: BreedSchema,
    purseMinor: PurseMinorSchema,
    currency: PlannerPurseCurrencySchema,
    season: SeasonSchema,
});
export const PlannerCsvImportCommandSchema = z
    .object({
    importId: IdSchema,
    title: persistedText(z.string().trim().min(2).max(200)),
    racecourseId: IdSchema,
    sourceFilename: z
        .string()
        .trim()
        .min(1)
        .max(180)
        .regex(/^[^/\\\u0000-\u001F\u007F]+\.csv$/iu),
    sourceSize: z.number().int().positive().max(PLANNER_CSV_MAX_SOURCE_BYTES),
    rows: z.array(RaceFieldsSchema).min(1).max(PLANNER_CSV_MAX_ROWS),
    reason: persistedText(z.string().trim().min(5).max(500)),
})
    .strict();
export const DraftValidationIssueSchema = z.enum([
    "RACE_DATE_INVALID",
    "GRADE_INVALID",
    "SURFACE_INVALID",
    "DISTANCE_INVALID",
    "BREED_INVALID",
    "PURSE_INVALID",
    "CURRENCY_INVALID",
    "SEASON_INVALID",
    "SOURCE_LOCATION_INVALID",
    "CONFIDENCE_INVALID",
]);
export const PlannerReviewStateSchema = z.enum([
    "AWAITING_HUMAN_ACTION",
    "READY_FOR_REVIEW",
    "IN_REVIEW",
]);
